import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Radio,
  message,
  Popconfirm,
  Typography,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CATEGORIES = [
  { value: 'basics',      label: '基础知识' },
  { value: 'technical',   label: '技术分析' },
  { value: 'fundamental', label: '基本面' },
  { value: 'risk',        label: '风险管理' },
  { value: 'psychology',  label: '交易心理' },
];

const DIFFICULTIES = [
  { value: 'easy',   label: '简单', color: 'green' },
  { value: 'medium', label: '中等', color: 'orange' },
  { value: 'hard',   label: '困难', color: 'red' },
];

const QuizManage = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [keyword, setKeyword] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/quiz/admin/questions', {
        params: {
          category: filterCategory,
          difficulty: filterDifficulty,
          keyword: keyword || undefined,
        },
      });
      if (res.data.success) setList(res.data.data.questions || []);
    } catch (e) {
      message.error('获取题目列表失败');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterDifficulty, keyword]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'basics',
      difficulty: 'easy',
      isActive: true,
      sortOrder: 0,
      correctAnswer: 0,
      options: ['', ''],
    });
    setModalOpen(true);
  };

  const parseOptions = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try { const p = JSON.parse(v); return Array.isArray(p) ? p : ['', '']; }
      catch (_) { return ['', '']; }
    }
    return ['', ''];
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      question: record.question,
      options: parseOptions(record.options),
      correctAnswer: record.correctAnswer,
      explanation: record.explanation || '',
      category: record.category,
      difficulty: record.difficulty,
      isActive: record.isActive,
      sortOrder: record.sortOrder || 0,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const options = (values.options || []).map((s) => String(s || '').trim()).filter(Boolean);
      if (options.length < 2) {
        message.error('至少需要 2 个非空选项');
        return;
      }
      if (values.correctAnswer >= options.length) {
        message.error('正确答案索引超出选项数');
        return;
      }
      const payload = { ...values, options };
      if (editing) {
        await api.put(`/quiz/admin/questions/${editing.id}`, payload);
        message.success('题目已更新');
      } else {
        await api.post('/quiz/admin/questions', payload);
        message.success('题目已创建');
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败：' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/quiz/admin/questions/${id}`);
      message.success('已删除');
      fetchList();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '题目',
      dataIndex: 'question',
      ellipsis: true,
      render: (v) => <Text style={{ maxWidth: 400 }}>{v}</Text>,
    },
    {
      title: '分类',
      dataIndex: 'category',
      width: 100,
      render: (v) => {
        const m = CATEGORIES.find((c) => c.value === v);
        return <Tag color="blue">{m?.label || v}</Tag>;
      },
    },
    {
      title: '难度',
      dataIndex: 'difficulty',
      width: 90,
      render: (v) => {
        const m = DIFFICULTIES.find((d) => d.value === v);
        return <Tag color={m?.color}>{m?.label || v}</Tag>;
      },
    },
    {
      title: '选项数',
      dataIndex: 'options',
      width: 80,
      render: (v) => parseOptions(v).length,
    },
    {
      title: '正确答案',
      dataIndex: 'correctAnswer',
      width: 90,
      render: (v) => <Text strong>{String.fromCharCode(65 + Number(v || 0))}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      width: 80,
      render: (v) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
    },
    {
      title: '操作',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="删除后无法恢复，确认？"
            onConfirm={() => handleDelete(record.id)}
            okText="删除" cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="股票知识问答题库"
        description="前端 /quiz 页面为访客提供闯关答题（无需登录）。此处可增删改题目，改动实时生效。"
      />

      <Card
        title={<Title level={4} style={{ margin: 0 }}>题库列表</Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新增题目
          </Button>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            placeholder="搜索题干关键词"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={fetchList}
            style={{ width: 240 }}
          />
          <Select
            value={filterCategory}
            onChange={setFilterCategory}
            style={{ width: 140 }}
            options={[{ value: 'all', label: '全部分类' }, ...CATEGORIES]}
          />
          <Select
            value={filterDifficulty}
            onChange={setFilterDifficulty}
            style={{ width: 140 }}
            options={[{ value: 'all', label: '全部难度' }, ...DIFFICULTIES.map((d) => ({ value: d.value, label: d.label }))]}
          />
          <Button onClick={fetchList}>刷新</Button>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? '编辑题目' : '新增题目'}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="保存"
        cancelText="取消"
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="question"
            label="题目"
            rules={[{ required: true, message: '请输入题目' }]}
          >
            <TextArea rows={3} placeholder="例如：什么是市盈率（P/E Ratio）？" />
          </Form.Item>

          <Form.Item
            label="选项列表"
            required
            extra="至少 2 项，最多 6 项。填写后在下方选择正确答案的字母。"
          >
            <Form.List name="options">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Space
                      key={field.key}
                      align="baseline"
                      style={{ display: 'flex', marginBottom: 8, width: '100%' }}
                    >
                      <Text strong style={{ width: 24 }}>
                        {String.fromCharCode(65 + index)}
                      </Text>
                      <Form.Item
                        {...field}
                        style={{ marginBottom: 0, flex: 1, minWidth: 480 }}
                        rules={[{ required: true, message: '不能为空' }]}
                      >
                        <Input placeholder={`选项 ${String.fromCharCode(65 + index)}`} />
                      </Form.Item>
                      {fields.length > 2 && (
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      )}
                    </Space>
                  ))}
                  {fields.length < 6 && (
                    <Button
                      type="dashed"
                      onClick={() => add('')}
                      icon={<PlusOutlined />}
                      size="small"
                    >
                      添加选项
                    </Button>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            name="correctAnswer"
            label="正确答案"
            rules={[{ required: true, message: '请选择正确答案' }]}
          >
            <Radio.Group>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Radio.Button key={i} value={i}>
                  {String.fromCharCode(65 + i)}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="explanation"
            label="答案解析"
            extra="用户答完后展示，解释为什么这是正确答案（可留空）"
          >
            <TextArea rows={3} placeholder="例如：P/E = 股价 / 每股收益..." />
          </Form.Item>

          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="category" label="分类" style={{ flex: 1 }}>
              <Select options={CATEGORIES} />
            </Form.Item>
            <Form.Item name="difficulty" label="难度" style={{ flex: 1 }}>
              <Select options={DIFFICULTIES.map((d) => ({ value: d.value, label: d.label }))} />
            </Form.Item>
            <Form.Item name="sortOrder" label="排序（大靠前）" style={{ width: 140 }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item name="isActive" label="启用" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default QuizManage;
